/* eslint-disable max-lines */
// about vuex
// The MIT License (MIT)
//
// Copyright (c) 2015-present Evan You
//
// Permission is hereby granted, free of charge, to any person obtaining a copy
// of this software and associated documentation files (the "Software"), to deal
// in the Software without restriction, including without limitation the rights
// to use, copy, modify, merge, publish, distribute, sublicense, and/or sell
// copies of the Software, and to permit persons to whom the Software is
// furnished to do so, subject to the following conditions:
//
// The above copyright notice and this permission notice shall be included in
// all copies or substantial portions of the Software.
//
// THE SOFTWARE IS PROVIDED "AS IS", WITHOUT WARRANTY OF ANY KIND, EXPRESS OR
// IMPLIED, INCLUDING BUT NOT LIMITED TO THE WARRANTIES OF MERCHANTABILITY,
// FITNESS FOR A PARTICULAR PURPOSE AND NONINFRINGEMENT. IN NO EVENT SHALL THE
// AUTHORS OR COPYRIGHT HOLDERS BE LIABLE FOR ANY CLAIM, DAMAGES OR OTHER
// LIABILITY, WHETHER IN AN ACTION OF CONTRACT, TORT OR OTHERWISE, ARISING FROM,
// OUT OF OR IN CONNECTION WITH THE SOFTWARE OR THE USE OR OTHER DEALINGS IN
// THE SOFTWARE.

import { fixNumberWithDecimal } from "../../../common/nodejs/mathFunctions";
import dmUtil from "../dataMonitor/dataMonitorUtil";
import { THIN, NORMAL, THICK, SOLID, DASHED, DOTTED } from '../../../share/util/Graph';

const TAB_KEY = [
  "RANGE", // 表示範囲
  "TIME_GRID", // 時間軸
  "LINE" // ライン設定
];

const TAB = {
  RANGE: 0,
  TIME_GRID: 1,
  LINE: 2
};

// 表示範囲
export const RANGE = {
  DEFAULT: 'default',
  AUTO: 'auto',
  MANUAL: 'manual',
};

// グラフ表示範囲がない場合のデフォルト
export const DEFAULT_RANGE_DISPLAY = { "min": 0.0, "max": 0.0 };

export default {
  name: "configureGraph",

  props: {
    // グラフ選択データ
    selectedGraphList: { type: Array, default: [], required: true },
    // 変換情報リスト
    conversionList: { type: Object, default: {}, required: true },
    // 画面パーツ情報
    displayPartsInfo: { type: Object, default: {}, required: true },

    // 既存のx軸設定情報
    xRange: { type: Number, default: undefined, required: false },
    // 既存のy軸設定情報
    yRange: { type: Object, default: undefined, required: false },
    // 既存のライン設定情報
    lineSetting: { type: Object, default: undefined, required: false },

    // 初期タブ
    initTab: { type: String, default: TAB_KEY[TAB.RANGE], required: false },

    // DM機能の設定インスタンス
    dataMonitorFunctionSettings: { type: Object, required: false },
  },

  components: {
  },

  data() {
    return {
      currentTab: this.initTab,
      // 時間軸（既存の設定値がある場合、その値を設定）
      xAxisGridWidth: (this.xRange !== undefined)
        ? this.xRange
        : this.displayPartsInfo[TAB_KEY[TAB.TIME_GRID]].list[this.displayPartsInfo[TAB_KEY[TAB.TIME_GRID]].initIndex],
      // 選択項目
      targetItemIndex: 0,
      // 選択項目の表示範囲（既存の設定値がある場合、その値を設定）
      selectedRange: (this.yRange !== undefined) && (this.selectedGraphList[0].data_monitor_id in this.yRange)
        ? this.yRange[this.selectedGraphList[0].data_monitor_id].selectedRange
        : RANGE.AUTO,
      // 選択項目の変換情報
      selectedConversion: this.getSelectedConversion(),
      // 選択項目のdid
      targetDid: this.getTargetDid(),
      // 手動レンジのMin入力値（既存の設定値がある場合、かつ手動レンジの場合、その値を設定）
      manualMinValue: ((this.yRange !== undefined)
        && (this.selectedGraphList[0].data_monitor_id in this.yRange)
        && (this.yRange[this.selectedGraphList[0].data_monitor_id].selectedRange === RANGE.MANUAL))
        ? this.yRange[this.selectedGraphList[0].data_monitor_id].min.toString()
        : "",
      // 手動レンジのMax入力値（既存の設定値がある場合、かつ手動レンジの場合、その値を設定）
      manualMaxValue: ((this.yRange !== undefined)
        && (this.selectedGraphList[0].data_monitor_id in this.yRange)
        && (this.yRange[this.selectedGraphList[0].data_monitor_id].selectedRange === RANGE.MANUAL))
        ? this.yRange[this.selectedGraphList[0].data_monitor_id].max.toString()
        : "",
      modalLoading: false,
      userSettingInfo: this.getUserSettingInfo(), // ユーザ設定値を保持する
      isAlert: {}, // 入力検証アラート情報を保持する
      changedLineSetting: {},
      selectedThickness: NORMAL, // デフォルトの線の太さ
      thicknessOptions: [
        THIN,
        NORMAL,
        THICK,
      ],
      selectedLineStyle: SOLID.join(), // デフォルトの線のスタイル
      convertedLineStyle: undefined,
      lineStyleOptions: [
        SOLID.join(),
        DASHED.join(),
        DOTTED.join(),
      ],
      lineColor: "#FF0000",
      previewLineCoordinates: {
        x1: 0,
        y1: 0,
        x2: 0,
        y2: 0,
      }
    };
  },

  watch: {
    currentTab: {
      handler(val) {
        if (val === TAB_KEY[TAB.LINE]) {
          this.updateLinePreview();
        }
      }
    }
  },

  methods: {

    /**
     * 変換情報の形式を調整する。
     * 本コンポーネントから派生した子コンポーネントを作成し、オーバーライドする前提。
     * サービスごとに変換情報の形式が異なるため、その形式の差異を本関数で吸収する。
     * @param {Object} propsConversions Propsにて渡されるオリジナルの変換情報
     * @returns 本コンポーネント用に形式を変更した変換情報
     */
    formatConversionList(propsConversions) {
      return propsConversions;
    },

    /**
     * TABキー取得
     */
    tabKey() {
      return TAB_KEY;
    },

    /**
     * TAB取得
     */
    tab() {
      return TAB;
    },

    /**
     * 表示範囲種別取得
     */
    range() {
      return RANGE;
    },

    /**
     * AUTOレンジのみの選択かどうか判定
     * @param {*} conv
     * @returns 
     */
    isOnlyAutoRange(conv) {
      if ((conv.graph_range_display.min === 0.0) && (conv.graph_range_display.max === 0.0)) {
        // min/maxの設定がない
        return true;
      }
      return false;

    },

    /**
     * OKボタン押下時処理
     */
    handleOkButtonClick() {
      if (this.validate()) {
        // 入力検証エラーの場合何もしない
        return false;
      }
      this.userSettingInfo.xRange = this.xAxisGridWidth;
      const formattedConversions = this.formatConversionList(this.conversionList);
      Object.keys(this.userSettingInfo.yRange).forEach((did) => {
        // 手動レンジ以外はdid表の値を設定する
        if (this.userSettingInfo.yRange[did].selectedRange !== RANGE.MANUAL) {
          this.userSettingInfo.yRange[did].min = formattedConversions[did].graph_range_display.min;
          this.userSettingInfo.yRange[did].max = formattedConversions[did].graph_range_display.max;
        }
      });
      this.userSettingInfo.lineSetting = this.changedLineSetting;
      this.$emit("handle-config-graph-complete", this.userSettingInfo);
      return true;
    },

    /**
     * キャンセルボタン押下時処理
     */
    handleCancelButtonClick() {
      // none
    },

    /**
     * 選択項目の変換情報取得
     * @param {*} targetIndex 
     * @returns object conversionInfo
     */
    getSelectedConversion(targetIndex = 0) {
      const self = this;
      const formattedConversions = self.formatConversionList(self.conversionList);
      const did = self.selectedGraphList[targetIndex].data_monitor_id;
      const rc = formattedConversions[did];
      return rc;
    },

    /**
     * 選択項目のdid取得
     * @param {*} targetIndex 
     * @returns string did
     */
    getTargetDid(targetIndex = 0) {
      return this.selectedGraphList[targetIndex].data_monitor_id;
    },

    /**
     * 小数点桁数にあわせた文字列変換
     * @param {*} val
     * @returns string value
     */
    getDigitValue(val) {
      const frac_digit = this.selectedConversion.digit_after_decimal;
      if ((frac_digit === undefined) || (frac_digit === null)) {
        return val.toString();
      }
      const value = fixNumberWithDecimal(Number(val), frac_digit);
      return value;
    },

    /**
     * 小数点桁数にあわせた刻み値取得
     * @returns string value
     */
    getStepValue() {
      const frac_digit = this.selectedConversion.digit_after_decimal;
      if ((frac_digit === undefined) || (frac_digit === null) || (frac_digit === 0)) {
        return "1";
      }
      return `0.${"0".repeat(frac_digit - 1)}1`;
    },

    /**
     * 項目選択プルダウンのリストを返す
     * @returns {array} 項目選択プルダウンのリスト
     */
    getPulldownOptions() {
      return this.selectedGraphList.map((x, i) => {
        // システム列がある場合は、プルダウンにもシステム名を付与する
        const tempSysId = dmUtil.getSystem(x.data_monitor_id);
        const optionText = this.dataMonitorFunctionSettings?.isSystemRow ? `[${dmUtil.getAbbrSysName(tempSysId)}] ${x.menu_title}` : x.menu_title;
        return { 'value': i, 'text': optionText };
      });
    },

    /**
     * 表示範囲変更時処理
     */
    handleChangeRadioButton() {
      this.clearAlert();
      this.setManualMinMaxValue();
      this.userSettingInfo.yRange[this.targetDid].selectedRange = this.selectedRange;
    },

    /**
     * 選択項目変更時処理
     */
    handleSelectionChanged() {
      this.clearAlert();
      // 選択項目にあわせて情報取得
      this.selectedConversion = this.getSelectedConversion(this.targetItemIndex);
      this.targetDid = this.selectedGraphList[this.targetItemIndex].data_monitor_id;
      if (this.isOnlyAutoRange(this.selectedConversion)) {
        this.selectedRange = RANGE.AUTO;
      } else {
        let range = RANGE.AUTO; // 設定がない場合（初期値）
        if (this.userSettingInfo) {
          // ユーザ設定値がある場合
          range = this.userSettingInfo.yRange[this.targetDid].selectedRange;
        } else if (this.yRange) {
          // ユーザ設定値がなく、既存の設定値がある場合
          range = this.yRange[this.targetDid].selectedRange;
        }
        this.selectedRange = range;
      }
      this.setManualMinMaxValue();
      this.setLineSetting();
    },

    /**
     * Min/Max入力値の初期値設定
     */
    setManualMinMaxValue() {
      if (this.selectedRange === RANGE.DEFAULT) {
        // did表の値
        this.manualMinValue = this.getDigitValue(this.selectedConversion.graph_range_display.min);
        this.manualMaxValue = this.getDigitValue(this.selectedConversion.graph_range_display.max);
      }
      else if (this.selectedRange === RANGE.MANUAL) {
        // ユーザ設定値
        let min = this.userSettingInfo.yRange[this.targetDid].min;
        let max = this.userSettingInfo.yRange[this.targetDid].max;

        // SP45/125：初めて手動レンジ設定する場合に、spMinが存在する場合はそれを設定する
        if (this.userSettingInfo.yRange[this.targetDid].spMin && !this.userSettingInfo.yRange[this.targetDid].isManual) {
          min = this.userSettingInfo.yRange[this.targetDid].spMin;
          // ユーザ設定値として保持する
          this.userSettingInfo.yRange[this.targetDid].min = min;
        }

        // SP45/125：初めて手動レンジ設定する場合に、spMaxが存在する場合はそれを設定する
        if (this.userSettingInfo.yRange[this.targetDid].spMax && !this.userSettingInfo.yRange[this.targetDid].isManual) {
          max = this.userSettingInfo.yRange[this.targetDid].spMax;
          // ユーザ設定値として保持する
          this.userSettingInfo.yRange[this.targetDid].max = max;
        }

        this.manualMinValue = this.getDigitValue(min);
        this.manualMaxValue = this.getDigitValue(max);
      }
    },

    setLineSetting() {
      if (this.changedLineSetting && this.changedLineSetting[this.targetDid]) {
        this.selectedThickness = this.changedLineSetting[this.targetDid].thickness;
        this.selectedLineStyle = this.changedLineSetting[this.targetDid].style;
        this.lineColor = this.changedLineSetting[this.targetDid].color;
      }
    },

    changeLineSetting() {
      if (!this.changedLineSetting[this.targetDid]) {
        Object.assign(this.changedLineSetting, {
          [this.targetDid]: {}
        });
      }
      this.changedLineSetting[this.targetDid].thickness = this.selectedThickness;
      this.changedLineSetting[this.targetDid].style = this.selectedLineStyle;
      this.changedLineSetting[this.targetDid].color = this.lineColor;
      if (this.lineSetting[this.targetDid].color !== this.lineColor && !this.lineSetting[this.targetDid].colorChanged) {
        this.changedLineSetting[this.targetDid].colorChanged = true;
      }
    },

    /**
     * ユーザ設定情報の初期情報取得
     * @returns object userSettingInfo
     *    xRange: x軸の値
     *    yRange[did]: {min:Min値(数値) , max:Max値(数値)}
     */
    getUserSettingInfo() {
      const yRangeInfo = {};
      const formattedConversions = this.formatConversionList(this.conversionList);
      this.selectedGraphList.forEach((x) => {
        const did = x.data_monitor_id;
        // min/maxが存在しないキーは除外する
        if (this.isOnlyAutoRange(formattedConversions[did])) {
          return;
        }

        let selectedRange = RANGE.AUTO;
        let min = formattedConversions[did].graph_range_display.min;
        let max = formattedConversions[did].graph_range_display.max;
        let spMin = null;
        let spMax = null;
        if ((this.yRange !== undefined) && (did in this.yRange)) {
          // 既存の設定情報が存在する場合
          selectedRange = this.yRange[did].selectedRange;
          min = this.yRange[did].min;
          max = this.yRange[did].max;
          spMin = this.yRange[did].spMin;
          spMax = this.yRange[did].spMax;
        }
        yRangeInfo[did] = {
          selectedRange: selectedRange,
          min: min,
          max: max,
          // SP45/125：特有のmin/maxがあれば保持する
          spMin: spMin,
          spMax: spMax,
          // グラフ設定初期化時に手動レンジが設定されていたかどうかの情報を保持
          isManual: selectedRange === RANGE.MANUAL
        };
      });
      const info = {
        xRange: this.displayPartsInfo[TAB_KEY[TAB.TIME_GRID]].initIndex,
        yRange: yRangeInfo
      };
      return info;
    },

    /**
     * Min入力値変更時処理
     */
    handleMinValueChanged() {
      // validate
      this.clearAlert();
      const requiredMsg = 'message.alert_validate_required';
      const patternMsg = 'message.alert_validate_invalid_pattern';
      if (!this.manualMinValue) {
        // 未入力の場合
        this.isAlert.manualMinValue = { valid: false, msg: requiredMsg, length: '' };
        return;
      }
      const val = Number(this.manualMinValue);
      if (val < Number.MIN_SAFE_INTEGER) {
        // 最小値未満の場合
        this.isAlert.manualMinValue = { valid: false, msg: patternMsg, length: '' };
        return;
      }
      if (val >= Number(this.manualMaxValue)) {
        // min >= maxの場合
        this.isAlert.manualMinValue = { valid: false, msg: patternMsg, length: '' };
        return;
      }

      this.userSettingInfo.yRange[this.targetDid].min = val;
    },

    /**
     * Max入力値変更時処理
     */
    handleMaxValueChanged() {
      // validate
      this.clearAlert();
      const requiredMsg = 'message.alert_validate_required';
      const patternMsg = 'message.alert_validate_invalid_pattern';
      if (!this.manualMaxValue) {
        // 未入力の場合
        this.isAlert.manualMaxValue = { valid: false, msg: requiredMsg, length: '' };
        return;
      }
      const val = Number(this.manualMaxValue);
      if (val > Number.MAX_SAFE_INTEGER) {
        // 最大値超過の場合
        this.isAlert.manualMaxValue = { valid: false, msg: patternMsg, length: '' };
        return;
      }
      if (val <= Number(this.manualMinValue)) {
        // min >= maxの場合
        this.isAlert.manualMaxValue = { valid: false, msg: patternMsg, length: '' };
        return;
      }

      this.userSettingInfo.yRange[this.targetDid].max = val;
    },

    /**
     * 入力検証エラー存在確認
     * @returns boolean fault
     */
    validate() {
      const fault = Object.keys(this.isAlert).some((key) => this.isAlert[key].valid === false);
      return fault;
    },

    /**
     * 入力検証アラートのクリア
     */
    clearAlert() {
      this.isAlert = {
        manualMinValue: { valid: null, msg: '' },
        manualMaxValue: { valid: null, msg: '' },
      };
    },

    /**
     * ライン設定のクリア
     */
    clearLineSetting() {
      this.changedLineSetting = structuredClone(this.lineSetting);
    },

    /**
      * ラインのプレビューを更新
      */
    updateLinePreview() {
      this.$nextTick(() => {
        const svg = document.getElementById('previewLine');
        if (!svg) return;
        const width = svg.clientWidth;
        const height = svg.clientHeight;
        this.previewLineCoordinates.x1 = width * 0.1;
        this.previewLineCoordinates.y1 = height * 0.5;
        this.previewLineCoordinates.x2 = width * 0.9;
        this.previewLineCoordinates.y2 = height * 0.5;
      });
    },

    /**
      * 選択済みのラインのスタイルを更新
      */
    selectLineStyle(lineStyle) {
      this.selectedLineStyle = lineStyle;
      this.changeLineSetting();
    },

    /**
      * 選択済みのラインの太さを更新
      */
    selectLineThickness(thickness) {
      this.selectedThickness = thickness;
      this.changeLineSetting();
    }
  },

  created() {
    this.clearAlert();
    this.setManualMinMaxValue();
    this.clearLineSetting();
    this.setLineSetting();
  },

  mounted() {
    // ウィンドウサイズに応じてSVG（ラインのプレビュー）の大きさを調整するために設定
    window.addEventListener('resize', this.updateLinePreview);
  },

  beforeDestroy() {
    window.removeEventListener('resize', this.updateLinePreview);
  }
};
